import { useCallback } from 'react';

import { VendorTaxonomyKey } from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getVendorTaxonomyId } from '@zola-helpers/client/dist/es/marketplace/vendorUtils';
import {
  BUSINESS_UNIT,
  COMPONENT,
} from '@zola-helpers/client/dist/es/tracking/onboard/onboardTrackingConstants';
import { SearchLocationView, SearchResultsSignupView } from '@zola/svc-marketplace-ts-types';
import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';
import H from '@zola/zola-ui/src/typography/Headings';
import P from '@zola/zola-ui/src/typography/Paragraphs';

import cx from 'classnames';

import { MarketplaceAuthModalV2Props } from '~/components/common/modals/MarketplaceAuthModalV2/MarketplaceAuthModalV2';
import { useModal } from '~/contexts/ModalContext';
import { useUserContext } from '~/contexts/UserContext';
import { useToast } from '~/hooks/useToast';
import ApiService from '~/util/apiService';
import { StandardLogFn } from '~/util/logger';
import { isGuest } from '~/util/userContextUtils';

import styles from './lowPlannerSupply.module.less';

export interface LowPlannerSupplyProps {
  taxonomyKey: VendorTaxonomyKey;
  location: SearchLocationView;
}

export const LowPlannerSupply = ({ taxonomyKey, location }: LowPlannerSupplyProps) => {
  const { showV2PredefinedModal, hideModal } = useModal();
  const { positiveToast, negativeToast } = useToast();
  const userContext = useUserContext();

  const searchResultsSignup = useCallback(() => {
    ApiService.post<SearchResultsSignupView>(
      '/web-marketplace-api/v1/storefront-search/search-results-signup',
      {
        taxonomyNodeId: getVendorTaxonomyId(taxonomyKey) as number,
        usCityId: location.usCityId as number,
      }
    )
      .then(() => {
        positiveToast({ headline: "You're on the list!" });
      })
      .catch((error) => {
        StandardLogFn(error);
        negativeToast({
          headline: 'An error occurred registering your interest. Please try again.',
        });
      });
  }, [location, negativeToast, positiveToast, taxonomyKey]);

  const showMarketplaceAuthModal = useCallback(() => {
    const modalProps: MarketplaceAuthModalV2Props = {
      loginCallback: () => null,
      signupCallback: () => null,
      onCompleteCallback: searchResultsSignup,
      authHed: 'Sign up and be the first to know',
      loginHed: 'Sign in and be the first to know',
      signupCTA: 'Submit',
      loginCTA: 'Submit',
      tooltipProps: {
        loginCopy: "Don't miss out! Sign in to be notified when we add new planners.",
        signupCopy: "Don't miss out! Sign up to be notified when we add new planners.",
      },
      trackingProps: {
        businessUnit: BUSINESS_UNIT.MARKETPLACE,
        component: COMPONENT.MARKETPLACE_COUPLES,
      },
      hideModalFn: hideModal,
    };

    showV2PredefinedModal<MarketplaceAuthModalV2Props>(
      'MARKETPLACE_AUTHV2',
      { dialogHeadingId: 'marketplace-auth-modal', triggerRef: null, closeOnOverlayClick: false },
      modalProps
    );
  }, [hideModal, showV2PredefinedModal, searchResultsSignup]);

  const joinList = useCallback(() => {
    if (!isGuest(userContext)) {
      searchResultsSignup();
    } else {
      showMarketplaceAuthModal();
    }
  }, [searchResultsSignup, showMarketplaceAuthModal, userContext]);

  return (
    <div className={cx(styles.lowPlannerSupply)} data-testid="low-supply-card">
      <H.Title4 className={styles.hed}>
        Get notified when we add new planners&nbsp;in&nbsp;this&nbsp;area
      </H.Title4>
      <P.BodyBase color="BLACK_075">
        Wedding planners are new to Zola. We&apos;re busy adding the best in the biz.
      </P.BodyBase>
      <ButtonV3 onClick={() => joinList()}>Join the list</ButtonV3>
    </div>
  );
};
